import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LangProvider, useLang } from "@/context/LangContext";
import Splash from "@/components/Splash";
import CreditModal from "@/components/CreditModal";
import FreeTimeGate from "@/components/FreeTimeGate";
import VerifyGate from "@/components/VerifyGate";
import { trackPageview } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Forgot from "@/pages/Forgot";
import Onboarding from "@/pages/Onboarding";
import ChooseLanguage from "@/pages/ChooseLanguage";
import HomePage from "@/pages/Home";
import Learn from "@/pages/Learn";
import Lesson from "@/pages/Lesson";
import Placement from "@/pages/Placement";
import ModuleReview from "@/pages/ModuleReview";
import Challenge from "@/pages/Challenge";
import Life from "@/pages/Life";
import LifeMission from "@/pages/LifeMission";
import Stories from "@/pages/Stories";
import Story from "@/pages/Story";
import Review from "@/pages/Review";
import MyWords from "@/pages/MyWords";
import Speaking from "@/pages/Speaking";
import SayItForMe from "@/pages/SayItForMe";
import ScanLearn from "@/pages/ScanLearn";
import Practice from "@/pages/Practice";
import MyPhrases from "@/pages/MyPhrases";
import Progress from "@/pages/Progress";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import SwitzerlandHub from "@/pages/SwitzerlandHub";
import Upgrade from "@/pages/Upgrade";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import TrialOffer from "@/pages/TrialOffer";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1EEFB]">
      <div className="w-10 h-10 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function AdminProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
  return children;
}

function PaywallListener() {
  const nav = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handler = (e) => {
      if (location.pathname === "/upgrade") return;
      toast.info(e.detail || "This is a Premium feature.");
      nav("/upgrade");
    };
    window.addEventListener("mel:paywall", handler);
    return () => window.removeEventListener("mel:paywall", handler);
  }, [nav, location.pathname]);
  return null;
}

function LangSync() {
  const { profile } = useAuth();
  const { setLang } = useLang();
  useEffect(() => {
    if (profile && profile.app_lang) setLang(profile.app_lang);
  }, [profile, setLang]);
  return null;
}

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageview();
  }, [location.pathname]);
  return null;
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  return (
    <ThemeProvider>
    <LangProvider>
    <AuthProvider>
      {showSplash && <Splash onDone={() => setShowSplash(false)} />}
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <PaywallListener />
        <CreditModal />
        <FreeTimeGate />
        <VerifyGate />
        <LangSync />
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/welcome" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
          <Route path="/choose-language" element={<Protected><ChooseLanguage /></Protected>} />
          <Route path="/start" element={<Protected><TrialOffer /></Protected>} />

          <Route path="/home" element={<Protected><HomePage /></Protected>} />
          <Route path="/learn" element={<Protected><Learn /></Protected>} />
          <Route path="/placement/:language" element={<Protected><Placement /></Protected>} />
          <Route path="/lesson/:id" element={<Protected><Lesson /></Protected>} />
          <Route path="/module-review/:id" element={<Protected><ModuleReview /></Protected>} />
          <Route path="/challenge" element={<Protected><Challenge /></Protected>} />
          <Route path="/life" element={<Protected><Life /></Protected>} />
          <Route path="/life/:id" element={<Protected><LifeMission /></Protected>} />
          <Route path="/stories" element={<Protected><Stories /></Protected>} />
          <Route path="/story/:id" element={<Protected><Story /></Protected>} />
          <Route path="/review" element={<Protected><Review /></Protected>} />
          <Route path="/words" element={<Protected><MyWords /></Protected>} />
          <Route path="/progress" element={<Protected><Progress /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />

          <Route path="/speaking/:sid" element={<Protected><Speaking /></Protected>} />
          <Route path="/say-it-for-me" element={<Protected><SayItForMe /></Protected>} />
          <Route path="/scan" element={<Protected><ScanLearn /></Protected>} />
          <Route path="/switzerland" element={<Protected><SwitzerlandHub /></Protected>} />
          <Route path="/practice" element={<Protected><Practice /></Protected>} />
          <Route path="/phrases" element={<Protected><MyPhrases /></Protected>} />
          <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />

          <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
            <Route index element={<AdminAnalytics />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  );
}

export default App;
